import React, { useState, useEffect } from 'react';
import { Center, Stack } from '@fiverr-private/layout_components';
import { Text, Typography } from '@fiverr-private/typography';
import { ChoiceGroup, ChoiceGroupItem } from '@fiverr-private/forms';
import { StoreType } from '@fiverr-private/store_builder_types';
import { useImpressionContainer, useImpressionItem } from '@fiverr-private/impressionable';
import { stepMetaMap, onImpression } from '@fiverr-private/store_builder_bi_events';
import { AppI18n } from '../../i18n';
import { useStoreData } from '../../hooks/useStoreData';
import { useAutoSave } from '../../hooks/useAutoSave';
import { useStepValidation } from '../../hooks/use-step-validation/useStepValidation';
import { useWizardNavigation } from '../../hooks/use-wizard-navigation/useWizardNavigation';
import { StepTitle } from '../../components/step-title/StepTitle';

const CHOICES: { value: StoreType; title: React.ReactNode; description: React.ReactNode }[] = [
    {
        value: StoreType.BRANDED_STORE,
        title: <AppI18n k="steps.choose_selling_plan.self.title" />,
        description: <AppI18n k="steps.choose_selling_plan.self.description" />,
    },
    {
        value: StoreType.DROPSHIPPER,
        title: <AppI18n k="steps.choose_selling_plan.dropshipping.title" />,
        description: <AppI18n k="steps.choose_selling_plan.dropshipping.description" />,
    },
];

export const ChooseSellingPlan: React.FC = () => {
    const { storeData } = useStoreData();
    const { autoSave } = useAutoSave();
    const { currentPath } = useWizardNavigation();
    const { registerStepValidation, unregisterStepValidation } = useStepValidation();
    const [value, setValue] = useState<StoreType | null>(storeData?.dropshipper || null);
    const [pageImpressionRef, pageImpressionId] = useImpressionContainer();

    useImpressionItem({
        selfRef: pageImpressionRef,
        selfId: pageImpressionId,
        sendImpressionToBQ: false,
        enrichment: {
            eventType: stepMetaMap[currentPath].impressionName,
            componentName: stepMetaMap[currentPath].name,
        },
        intersectionThreshold: 0,
        onImpression,
    });

    useEffect(() => {
        const validationStatusFn = () => (value ? 'valid' : 'invalid');

        registerStepValidation('choose-selling-plan', validationStatusFn);

        return () => {
            unregisterStepValidation('choose-selling-plan');
        };
    }, [value, registerStepValidation, unregisterStepValidation]);

    const handleSellingPlanChange = (selectedValue: StoreType | null) => {
        setValue(selectedValue);

        if (selectedValue) {
            autoSave({ dropshipper: selectedValue as StoreType });
        }
    };

    return (
        <Center>
            <Stack ref={pageImpressionRef} direction="column" justifyContent="flexStart" gap="8">
                <StepTitle title={<AppI18n k="steps.choose_selling_plan.title" />} />
                <ChoiceGroup multiple={false} direction="column" value={value} onChange={handleSellingPlanChange}>
                    {CHOICES.map((choice) => (
                        <ChoiceGroupItem key={choice.value} value={choice.value}>
                            <Stack direction="column" margin="3" gap="0.5" width={{ default: 265, sm: 350 }}>
                                <Typography fontWeight="semibold">{choice.title}</Typography>
                                <Text>{choice.description}</Text>
                            </Stack>
                        </ChoiceGroupItem>
                    ))}
                </ChoiceGroup>
            </Stack>
        </Center>
    );
};

ChooseSellingPlan.displayName = 'ChooseSellingPlan';
